<template>
  <div
    class="review-star-rating"
    :class="`review-star-rating_size_${size}`"
    role="radiogroup"
    :aria-label="rootAriaLabel"
  >
    <button
      v-for="star in 5"
      :key="star"
      type="button"
      class="review-star-rating__star"
      :class="{ 'review-star-rating__star_active': star <= (hovered || value) }"
      :aria-label="t('starAriaLabel', { star })"
      :disabled="disabled"
      @mouseenter="hovered = star"
      @mouseleave="hovered = 0"
      @focus="hovered = star"
      @blur="hovered = 0"
      @click="emit('select', star)"
    >
      <StarIcon />
    </button>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from '@experience/i18n';
import { computed, ref } from 'vue';

// ?skipsvgo сохраняет viewBox иконки (иначе звёзды схлопываются), не завязываясь на глобальный svgoConfig в vite.config
import StarIcon from '@/shared/icons/svg/star-rating.svg?skipsvgo';

import ru from '../i18n/ru';

const props = withDefaults(
  defineProps<{
    value?: number;
    disabled?: boolean;
    ariaLabel?: string;
    size?: 'm' | 'l';
  }>(),
  {
    value: 0,
    disabled: false,
    ariaLabel: undefined,
    size: 'm',
  },
);

const emit = defineEmits<{
  select: [rate: number];
}>();

const hovered = ref(0);

type MessageSchema = typeof ru;
const { t } = useI18n<{ message: MessageSchema }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const rootAriaLabel = computed(() => props.ariaLabel ?? t('ratingLabel'));

const iconSize = computed(() => {
  if (props.size === 'l') {
    return 40;
  }

  return 28;
});

const iconSizePx = computed(() => `${String(iconSize.value)}px`);
</script>

<style lang="scss" scoped>
.review-star-rating {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.review-star-rating__star {
  box-sizing: content-box;
  display: inline-flex;
  width: v-bind('iconSizePx');
  height: v-bind('iconSizePx');
  padding: var(--alias-space-4);
  line-height: 0;
  color: var(--alias-color-icon-disabled);
  cursor: pointer;
  background: transparent;
  border: 0;

  &_active {
    color: var(--alias-color-icon-accent-red);
  }

  :deep(svg) {
    width: v-bind('iconSizePx');
    height: v-bind('iconSizePx');
  }

  :deep(path) {
    fill: currentcolor;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}
</style>
